/**
 * RTK Query slice for the synthetic-migration EVIDENCE run (DEV-193).
 *
 * Only a start mutation lives here — the run records are read via the generic
 * `things` endpoint (access pattern `SaGetSstv2EvidenceRuns`), polled by the
 * tab. Super-admin gated server-side by the `isAllowed` on fnThingsV2's
 * `/proxy/sirsteward-v2` route.
 *
 *   startSstv2EvidenceRun → POST /proxy/sirsteward-v2?resourceType=startEvidenceRun
 */
import { createApi } from "@reduxjs/toolkit/query/react"
import { Sstv2EvidenceSeedPlan } from "shared/sstv2MigrationEvidenceData"
import { baseQuery } from "../baseQuerySl2"

export type StartEvidenceRunArgs = {
  /**
   * SL1 source locker to drive — REQUIRED, 24 hex chars. There is no default on
   * either tier (LOCKER-344): the backend 400s a request without it rather than
   * falling back to the shared ssdev demo locker.
   */
  sourceLocker: string
  reseed?: boolean
  targetTenantId?: string
  runRollback?: boolean
  /** Total demo reservations to create; split across statuses by default weights. */
  reservationCount?: number
  /** Exact per-status counts. When set (and non-zero) it overrides the total. */
  distribution?: Sstv2EvidenceSeedPlan
}

export type StartEvidenceRunResult = {
  data: {
    started: boolean
    runId?: string
    reason?: string
    error?: string
    activeRunId?: string
    activeStatus?: string
  }
}

export const sstv2EvidenceApi = createApi({
  reducerPath: "sstv2EvidenceApi",
  baseQuery,
  endpoints: (builder) => ({
    startSstv2EvidenceRun: builder.mutation<
      StartEvidenceRunResult,
      StartEvidenceRunArgs
    >({
      query: (arg) => ({
        url: "proxy/sirsteward-v2",
        method: "POST",
        params: { resourceType: "startEvidenceRun" },
        body: {
          sourceLocker: arg.sourceLocker,
          reseed: arg?.reseed ?? true,
          runRollback: arg?.runRollback ?? true,
          ...(arg?.targetTenantId
            ? { targetTenantId: arg.targetTenantId }
            : {}),
          // Reseed sizing. `distribution` (exact per-status counts) wins over
          // `reservationCount` server-side; both are re-clamped there, so what
          // the FE sends is a request, not a guarantee.
          ...(arg?.reservationCount !== undefined
            ? { reservationCount: arg.reservationCount }
            : {}),
          ...(arg?.distribution ? { distribution: arg.distribution } : {}),
        },
      }),
    }),
    cancelSstv2EvidenceRun: builder.mutation<
      { data: { cancelled: boolean; runId?: string; reason?: string } },
      void
    >({
      query: () => ({
        url: "proxy/sirsteward-v2",
        method: "POST",
        params: { resourceType: "cancelEvidenceRun" },
        body: {},
      }),
    }),
  }),
})

export const {
  useStartSstv2EvidenceRunMutation,
  useCancelSstv2EvidenceRunMutation,
} = sstv2EvidenceApi
